import type { ReservoirWallet } from "@reservoir0x/reservoir-sdk";
import { useMemo } from "react";
import { type Hex, getAddress, hexToBigInt } from "viem";
import { useAccount, useSendTransaction, useSignMessage, useSignTypedData } from "wagmi";

export function useGetReservoirWallet() {
    const { sendTransactionAsync } = useSendTransaction();
    const { signTypedDataAsync } = useSignTypedData();
    const { signMessageAsync } = useSignMessage();
    const { address, chainId, status } = useAccount();

    const reservoirWallet: { wallet: ReservoirWallet; address: string } | undefined | null = useMemo(() => {
        if (address == null && status === "disconnected") {
            return null;
        }

        if (address == null) {
            return undefined;
        }

        return {
            address,
            wallet: {
                address: async () => address ?? "0x00",
                handleSignMessageStep: async (stepItem) => {
                    const signData = stepItem.data?.sign;
                    let signature: string | undefined;
                    if (signData) {
                        if (signData.signatureKind === "eip191") {
                            if (signData.message.match(/0x[0-9a-fA-F]{64}/)) {
                                // If the message represents a hash, we need to convert it to raw bytes first
                                signature = await signMessageAsync({
                                    message: {
                                        raw: signData.message as Hex,
                                    },
                                });
                            } else {
                                signature = await signMessageAsync({
                                    message: signData.message,
                                });
                            }
                        } else if (signData.signatureKind === "eip712") {
                            signature = await signTypedDataAsync({
                                domain: signData.domain,
                                types: signData.types,
                                primaryType: signData.primaryType,
                                message: signData.value,
                            });
                        }
                    }
                    return signature;
                },
                handleSendTransactionStep: async (localChainId, stepItem) => {
                    const stepData = stepItem.data;

                    if (getAddress(stepData.from) !== address) throw new Error("Invalid from address");
                    if (chainId !== localChainId) throw new Error("Invalid chainId");

                    return await sendTransactionAsync({
                        to: stepData.to,
                        value: hexToBigInt((stepData.value as Hex) || "0x00"),
                        data: stepData.data,
                    });
                },
            },
        };
    }, [sendTransactionAsync, signTypedDataAsync, signMessageAsync, address, chainId, status]);

    return reservoirWallet;
}
